import { Maximize2 } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { isSessionLate, sessionTone } from "@/lib/multi-view";
import type { BatchScale } from "@/lib/recipes/staged-method";
import type { CookingSession } from "@/types/cooking-session";
import { ProgressBar } from "./ProgressBar";
import { useBatchSizeLabel } from "./useBatchSizeLabel";
import { SessionActions } from "./SessionActions";
import { ChefLabel } from "./SessionFacts";
import { SessionStatusPill } from "./SessionStatusPill";
import { SessionTimer } from "./SessionTimer";
import { StageList } from "./StageList";

interface RecipeCardProps {
  session: CookingSession;
  seconds: number;
  scale: BatchScale;
  onExpand: () => void;
  onError: (message: string) => void;
}

/**
 * One recipe in the Grid (YC-25): name, chef and batch, the stage it is on, the
 * big timer with the time left, a progress bar, and the ingredients beside the
 * procedure for every stage. A late recipe takes a red border and a red timer.
 */
export function RecipeCard({ session, seconds, scale, onExpand, onError }: RecipeCardProps) {
  const { t } = useLingui();
  const batchLabel = useBatchSizeLabel();
  const late = isSessionLate(session, seconds);

  return (
    <article
      aria-label={session.recipeName}
      data-late={late ? "true" : undefined}
      className={cn(
        "flex min-h-0 flex-col gap-3 rounded-2xl border-2 bg-(--color-surface-content) p-4",
        late ? "border-(--color-danger)" : "border-(--color-border)",
      )}
    >
      <header className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-1">
          <h3 className="truncate text-lg font-semibold text-(--color-text-primary)">{session.recipeName}</h3>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <ChefLabel name={session.chefName} />
            <span className="text-xs font-medium text-(--color-text-secondary)">{t`Batch: ${batchLabel(scale)}`}</span>
          </div>
        </div>
        <Button type="button" variant="brand-outline" size="icon" onClick={onExpand} aria-label={t`Expand ${session.recipeName}`}>
          <Maximize2 />
        </Button>
      </header>

      <div className="flex items-end justify-between gap-2">
        <SessionTimer session={session} seconds={seconds} />
        <div className="flex flex-col items-end gap-1">
          <SessionStatusPill tone={sessionTone(session, seconds)} />
          <span className="text-xs text-(--color-text-secondary)">{session.currentStage?.name ?? t`No stage yet`}</span>
        </div>
      </div>
      <ProgressBar seconds={seconds} targetMinutes={session.targetMinutes} late={late} />

      <div className="min-h-0 flex-1 overflow-y-auto">
        <StageList session={session} scale={scale} />
      </div>

      <SessionActions session={session} onError={onError} />
    </article>
  );
}
